import { useEffect, useRef, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import './PropertyStreetView.css';

// Shared with PropertyGoogleMap.js's useJsApiLoader call - @react-google-maps/api
// dedupes script loading across multiple hook call sites that share this id,
// so opening the fullscreen gallery's Map and Street view tabs one after
// another doesn't load the SDK twice.
const GOOGLE_MAPS_SCRIPT_ID = 'naijanest-google-maps-script';
const GOOGLE_MAPS_LIBRARIES = [];

function PropertyStreetView({ latitude, longitude }) {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const containerRef = useRef(null);
  // 'checking' | 'available' | 'unavailable' - coverage is sparse for many
  // Nigerian addresses outside major cities, so this is checked explicitly
  // rather than assuming a panorama will always load.
  const [coverageStatus, setCoverageStatus] = useState('checking');

  useEffect(() => {
    if (!isLoaded || !window.google) {
      return undefined;
    }
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      setCoverageStatus('unavailable');
      return undefined;
    }

    let cancelled = false;
    setCoverageStatus('checking');
    const streetViewService = new window.google.maps.StreetViewService();
    streetViewService.getPanorama(
      { location: { lat: latitude, lng: longitude }, radius: 50 },
      (_data, status) => {
        if (cancelled) {
          return;
        }
        setCoverageStatus(status === window.google.maps.StreetViewStatus.OK ? 'available' : 'unavailable');
      }
    );

    return () => {
      cancelled = true;
    };
  }, [isLoaded, latitude, longitude]);

  useEffect(() => {
    if (coverageStatus !== 'available' || !containerRef.current || !window.google) {
      return;
    }
    // eslint-disable-next-line no-new
    new window.google.maps.StreetViewPanorama(containerRef.current, {
      position: { lat: latitude, lng: longitude },
      addressControl: false,
      showRoadLabels: false,
      fullscreenControl: false,
      keyboardShortcuts: false,
    });
  }, [coverageStatus, latitude, longitude]);

  if (!isLoaded || coverageStatus === 'checking') {
    return <div className="property-map-shell property-street-view-shell" />;
  }

  if (coverageStatus === 'unavailable') {
    return (
      <div className="property-map-shell">
        <div className="map-empty-state map-empty-state--standalone">
          <p>Street view isn&apos;t available for this location yet.</p>
          <small>Google hasn&apos;t captured street-level imagery here.</small>
        </div>
      </div>
    );
  }

  return (
    <div className="property-map-shell property-street-view-shell">
      <div ref={containerRef} className="property-street-view" />
    </div>
  );
}

export default PropertyStreetView;
